/**
 * Single source of truth for ERP registry environment display in the admin UI.
 *
 * The `environment` attribute is a nullable, enum-backed string on the origami
 * API (`production | preprod | recette | staging | test | development`).
 * Every place that shows it — the registry form select, the registries list
 * badge, the registry detail page, the workflow ERP picker — reads its labels
 * and badge variants from here, so a label change or a new environment value
 * is a one-line change in a single file.
 *
 * The labels themselves live in `@/shared/erp/erpEnvironment` (claimflow's
 * non-production banner shows the same wording) and are re-exported below.
 */

import type { BadgeVariant } from '@components/base/display';
import { ERP_ENVIRONMENT_LABELS } from '@/shared/erp/erpEnvironment';
import type { ErpEnvironment } from '../api/erpRegistriesService';

/** Human-readable label for every environment value. */
export { ERP_ENVIRONMENT_LABELS };

/**
 * Select items for the registry form, in declaration order
 * (most to least production-like).
 */
export const ERP_ENVIRONMENT_OPTIONS: ReadonlyArray<{
  value: ErpEnvironment;
  label: string;
}> = [
  { value: 'production', label: ERP_ENVIRONMENT_LABELS.production },
  { value: 'preprod', label: ERP_ENVIRONMENT_LABELS.preprod },
  { value: 'recette', label: ERP_ENVIRONMENT_LABELS.recette },
  { value: 'staging', label: ERP_ENVIRONMENT_LABELS.staging },
  { value: 'test', label: ERP_ENVIRONMENT_LABELS.test },
  { value: 'development', label: ERP_ENVIRONMENT_LABELS.development },
];

/**
 * The "no environment" select item.
 *
 * `environment` is nullable, so the select needs an explicit way back to the
 * unset state once a value has been picked (the base `Select` has no clear
 * affordance of its own). The empty string it submits reaches the API as
 * `null` — origami runs Laravel's `ConvertEmptyStringsToNull` middleware on
 * the admin routes.
 */
export const ERP_ENVIRONMENT_NONE_OPTION: { value: ''; label: string } = {
  value: '',
  label: '—',
};

/** Placeholder shown by the select while no environment is chosen. */
export const ERP_ENVIRONMENT_PLACEHOLDER = 'No environment';

/** Badge variants keyed by environment. */
const ERP_ENVIRONMENT_BADGE_VARIANTS: Record<ErpEnvironment, BadgeVariant> = {
  // Production is the one that warrants a second look before acting on it.
  production: 'warning',
  preprod: 'info',
  recette: 'info',
  staging: 'info',
  test: 'default',
  development: 'default',
};

/**
 * Returns the Badge variant used to render an environment.
 *
 * @param environment - The registry environment value.
 */
export function getErpEnvironmentBadgeVariant(
  environment: ErpEnvironment,
): BadgeVariant {
  return ERP_ENVIRONMENT_BADGE_VARIANTS[environment];
}
